/**
 * Карточка лида: поля лида и вся лента — звонки (запись, расшифровка), чаты,
 * мессенджеры, дела, стадии, комментарии. Лид со сделкой сразу открывает
 * карточку сделки. Спека `docs/specs/2026-09-25-lead-card-design.md`; те же
 * данные — `prokat-runner lead-card ID`.
 */
import { useQuery } from "@tanstack/react-query";
import { useState } from "react";
import { Navigate, useParams } from "react-router";

import { LeadLink } from "@/components/LeadLink";
import { Stat, Timeline, dt } from "@/components/timeline/Timeline";
import { type LeadCard, type LeadField, loadLeadCard } from "@/lib/leadCard";
import { NavMenu } from "@/nav/NavMenu";
import { LeadPodbor } from "@/pages/DealPodbor";

function Fields({ rows }: { rows: LeadField[] }) {
  const [all, setAll] = useState(false);
  const main = rows.filter((f) => f.main);
  const rest = rows.filter((f) => !f.main);
  const shown = all ? rows : main;
  return (
    <section className="rounded-[var(--radius-card)] border border-border bg-bg-subtle p-3 text-[12.5px]" data-annot="поля лида">
      <dl className="grid gap-x-4 gap-y-1 sm:grid-cols-[max-content_1fr]">
        {shown.map((f) => (
          <div key={f.code} className="contents">
            <dt className="text-fg-muted" title={f.code}>{f.label}</dt>
            <dd className="min-w-0 whitespace-pre-wrap break-words">{f.value}</dd>
          </div>
        ))}
      </dl>
      {rest.length > 0 && (
        <button type="button" onClick={() => setAll(!all)} aria-expanded={all} className="mt-2 text-[12px] text-accent hover:underline">
          {all ? "Только основные" : `Все поля (+${rest.length})`}
        </button>
      )}
    </section>
  );
}

function Body({ c }: { c: LeadCard }) {
  const l = c.lead;
  const s = c.summary!;
  return (
    <>
      <section className="rounded-[var(--radius-card)] border border-border bg-bg-subtle p-3 text-[12.5px]" data-annot="лид">
        <div className="mb-1 flex flex-wrap items-baseline gap-x-2 text-[13px] font-medium">
          <span>Лид</span>
          <LeadLink id={l.id} />
          <span>· {l.title ?? ""}</span>
          {c.deleted && <span className="text-[12px] font-normal text-err">удалён в CRM</span>}
        </div>
        <NavMenu r={{ obj: "leadID", id: l.id }} />
        <div className="text-fg-muted">
          {l.source ?? "—"} · {l.status ?? "—"} · {l.assigned ?? "—"} · создан {dt(l.created)}
        </div>
        <div className="font-mono text-fg-muted">{c.phones?.join(", ") || "номера нет"}</div>
      </section>
      {(c.errors?.length ?? 0) > 0 && (
        <div className="rounded-[var(--radius-card)] border border-warn p-2 text-[12.5px] text-warn">
          Карточка неполная: {c.errors!.join("; ")}
        </div>
      )}
      {(c.fields?.length ?? 0) > 0 && <Fields rows={c.fields!} />}
      <div className="grid grid-cols-2 gap-2 sm:grid-cols-6">
        <Stat label="Исходящих" value={s.calls_out} hint={`с разговором ${s.calls_out_talk}`} />
        <Stat label="Входящих разговоров" value={s.calls_in_talk} />
        <Stat label="Чатов" value={s.chats} hint="открытые линии Bitrix" />
        <Stat label="Мессенджеры" value={s.messenger_chats} hint={`чатов · сообщений ${s.messenger_msgs}`} />
        <Stat label="Звонков по номеру" value={s.calls_by_phone} hint="без привязки к лиду" />
        <Stat label="Первый контакт" value={s.first_contact ? dt(s.first_contact) : "не было"} hint="разговор или ответ в чате" />
      </div>
      <LeadPodbor leadId={l.id} />
      <Timeline events={c.events ?? []} scopes={[]} />
    </>
  );
}

export function LeadCardPage() {
  const leadId = Number(useParams().leadId) || 0;
  const q = useQuery({ queryKey: ["lead-card", leadId], queryFn: () => loadLeadCard(leadId), enabled: leadId > 0 });
  if (q.data?.redirect) return <Navigate to={`/deals/${q.data.redirect}`} replace />;
  return (
    <main className="flex min-h-0 flex-1 flex-col gap-4 overflow-y-auto p-5">
      {!leadId ? (
        <div className="text-[12.5px] text-fg-muted">Нет номера лида.</div>
      ) : q.isPending ? (
        <div className="text-[12.5px] text-fg-muted">Собираю лид из Bitrix и CRM…</div>
      ) : q.isError ? (
        <div className="text-[12.5px] text-err">Не удалось открыть лид: {String(q.error.message)}</div>
      ) : (
        <Body c={q.data} />
      )}
    </main>
  );
}
